import React from 'react';
import { useIntl } from 'react-intl';
import { StyledTooltip } from '../utils/TableStyles';

/**
 * Barra de botones de una grilla: la del extremo derecho del toolbar, con el reset, el export,
 * el selector de columnas y lo que cada pantalla necesite.
 *
 * Está copiada como `ButtonsTableTop` en tickets, móviles y disponibilidad (70 a 97 líneas
 * cada una), y lo que se repite no es la lista de botones —que varía por grilla, a propósito—
 * sino **el contenedor y la forma de cada botón**: un `StyledTooltip` envolviendo un `button`
 * con las clases del template y un `span` con el icono, nueve líneas cada vez.
 *
 * Por eso son dos piezas y no una:
 *
 *     <GridToolbar>
 *       <GridToolbarButton messageId="…" label="Restaurar" onClick={reset} icon="ft-rotate-ccw" />
 *       <GridColumnSelector … />
 *       <div className="dropdown">…el export propio de esta grilla…</div>
 *     </GridToolbar>
 *
 * Lo que no encaje en `GridToolbarButton` (un dropdown, un input file, un menú) va como hijo
 * directo del toolbar. El contenedor no impone nada sobre sus hijos.
 */

export const GridToolbar = (props) => (
	<div className={'dt-buttons btn-group' + (props.className ? ' ' + props.className : '')}>{props.children}</div>
);

/**
 * Un botón del toolbar, con el tooltip y las clases del template.
 *
 * @param messageId  key i18n del tooltip; `label` es su defaultMessage. `title` lo pisa con
 *                   un texto ya resuelto.
 * @param icon       clase del icono (`ft-upload`, `la la-columns`…). Para un icono que no es
 *                   una clase —un `img`, por ejemplo— usar `children`.
 * @param children   contenido del botón, alternativa a `icon`.
 * @param dropdownToggle  el botón abre un `.dropdown-menu` de Bootstrap (el export de tickets,
 *                   el importar de móviles). Sin esto el menú no abre: lo dispara el
 *                   `data-toggle`, no un onClick. No agrega la clase `dropdown-toggle` —la
 *                   flechita— porque no todas las grillas la usan.
 * @param id         id del botón, para el `aria-labelledby` del menú que abre.
 */
export const GridToolbarButton = (props) => {
	const intl = useIntl();

	const titulo = props.title
		? props.title
		: intl.formatMessage({ id: props.messageId, defaultMessage: props.label });

	const boton = (
		<button
			type="button"
			id={props.id}
			className={props.className || 'btn btn-secondary buttons-collection'}
			onClick={props.onClick}
			disabled={props.disabled}
			// El tooltip da el nombre accesible mientras está abierto; el aria-label lo da
			// siempre, que es lo que necesita un botón que solo muestra un icono.
			aria-label={titulo}
			data-toggle={props.dropdownToggle ? 'dropdown' : undefined}
			aria-haspopup={props.dropdownToggle ? true : undefined}
			aria-expanded={props.dropdownToggle ? false : undefined}
		>
			<span>{props.children ? props.children : <i className={props.icon}></i>}</span>
		</button>
	);

	// Un botón deshabilitado no dispara los eventos que abren el tooltip de MUI, así que
	// envolverlo solo agregaría un nodo que no hace nada.
	if (props.disabled) return boton;

	return (
		<StyledTooltip title={titulo} arrow placement="top">
			{boton}
		</StyledTooltip>
	);
};

export default GridToolbar;
